import React from 'react';
import { useSelector } from 'react-redux';
import { Popover, Card, Button, Form, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import * as Yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';

import { AsyncDispatch } from '../../../../types/global';
import { ApplicationState } from '../../../reducers';
import { AttributeTranslation } from '../../../../types/attributes';
import { typingDone } from '../../../utils/Utils';
import { fetchTranslations } from '../../../actions/parent/language/fetch';
import { LanguageTranslation } from '../../../../types/language';
import FormInput from '../../global/Forms/FormInput';
import { hasPermission } from '../../../utils/Permissions';
import AttributeTranslationTable from './AttributeTranslationTable';
import { useAppDispatch } from '../../../store/ReduxStore';

type FormValues = {
  attributeName: string;
};

type AttributeNamePopoverProps = {
  attributeName?: string;
  translations?: AttributeTranslation[];
  handleChange: (attributeName: string) => void;
  warnText?: string;
  detailedConfirmWarnText?: string;
  isSearchKeyword?: boolean;
  children?: React.ReactNode;
};

const AttributeNamePopover: React.FC<AttributeNamePopoverProps> = props => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const navigate = useNavigate();

  const [valueTranslations, setValueTranslations] = React.useState(props.translations);
  const [popoverVisible, setPopoverVisible] = React.useState(false);

  const user = useSelector((state: ApplicationState) => state.user.user);
  const canManageTranslations = hasPermission(user, 'can_manage_translation');

  const validationSchema = Yup.object().shape({
    attributeName: Yup.string().required(t('validation:required')),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      attributeName: props.attributeName || '',
    },
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, reset, formState, getValues } = formMethods;

  React.useEffect(() => {
    if (!popoverVisible) {
      reset({
        attributeName: props.attributeName || '',
      });
    }
  }, [popoverVisible, props.attributeName, reset]);

  const onSubmit = (values: FormValues) => {
    props.handleChange(values.attributeName);
    setPopoverVisible(false);
  };

  const handleChangedAttributeName = (name: string) => {
    setValue('attributeName', name, { shouldDirty: true });
    typingDone(() =>
      dispatch(fetchTranslations({ translationTypeId: 1, keywords: name })).then(response => {
        const translation = response.value.data.find((data: LanguageTranslation) =>
          data.translations?.find(t => t.language_id === 1 && t.value === name)
        );
        setValueTranslations(translation?.translations || []);
      })
    );
  };

  const okButton = (
    <Button
      data-testid="ok"
      type="primary"
      onClick={e => {
        if (!props.warnText) handleSubmit(onSubmit)();
        e.stopPropagation();
      }}
      disabled={
        props.isSearchKeyword ? getValues('attributeName').length === 0 : !formState.isDirty
      }
    >
      {t('common:ok')}
    </Button>
  );

  return (
    <Popover
      open={popoverVisible}
      onOpenChange={visible => {
        setPopoverVisible(visible);
      }}
      overlayClassName="help attribute_popover"
      destroyTooltipOnHide
      content={
        <Card className="attribute__form" onClick={e => e.stopPropagation()} bordered={false}>
          <FormProvider {...formMethods}>
            <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
              <div className="flex flex-col attribute-name">
                <FormInput
                  id="attributeValueInput"
                  name="attributeName"
                  label={t('attributes:attribute')}
                  handleChange={e => handleChangedAttributeName(e.target.value || '')}
                  required
                  useHookForm
                />
                {formState.isDirty && <div className="attribute__warn-text">{props.warnText}</div>}
              </div>
              {canManageTranslations && (
                <React.Fragment>
                  <div className="flex justify-between">
                    <div>{t('attributes:translations')}</div>
                    <Popconfirm
                      title={t('attributes:confirmSwitchToTanslations')}
                      okText={t('common:yes')}
                      cancelText={t('common:cancel')}
                      onConfirm={() =>
                        navigate('/settings/translations/translation-settings', {
                          state: { segmentId: 1 },
                        })
                      }
                    >
                      <Button size="small" className="truncate">
                        {t('attributes:editTranslations')}
                      </Button>
                    </Popconfirm>
                  </div>
                  <div className="attribute-translation-table">
                    <AttributeTranslationTable translations={valueTranslations || []} />
                  </div>
                </React.Fragment>
              )}
              <div className="flex-col items-end mt-2">
                <div>
                  <Button
                    onClick={e => {
                      setPopoverVisible(false);
                      e.stopPropagation();
                    }}
                    className="drawer-submit__bottom-cancel"
                  >
                    {t('common:cancel')}
                  </Button>
                  {props.warnText && (
                    <Popconfirm
                      title={props.detailedConfirmWarnText}
                      okText={t('common:confirm')}
                      cancelText={t('common:cancel')}
                      onConfirm={() => handleSubmit(onSubmit)()}
                    >
                      {okButton}
                    </Popconfirm>
                  )}
                  {!props.warnText && okButton}
                </div>
              </div>
            </Form>
          </FormProvider>
        </Card>
      }
      trigger="click"
    >
      <div
        className={classNames('pr-1', { attribute__popover_visible: popoverVisible })}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        {props.children}
      </div>
    </Popover>
  );
};

export default AttributeNamePopover;
